import { lazy, Suspense } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { CustomProvider, Loader } from "rsuite";
import esES from "rsuite/locales/es_ES";
import "rsuite/dist/rsuite.min.css";
import "./App.css";

// Pages (lazy)
const Login = lazy(() => import("./pages/Login"));
const Home = lazy(() => import("./pages/Home"));
import { ProtectedRoute } from "./components/ProtectedRoute";
import Layout from "./components/Layout";

// Importar páginas de cada módulo (lazy)
const RecursosPage = lazy(() =>
  import("./modules/recursos").then((m) => ({ default: m.RecursosPage }))
);
const ProductosPage = lazy(() =>
  import("./modules/productos").then((m) => ({ default: m.ProductosPage }))
);
const ProductosProfesionalesPage = lazy(() =>
  import("./modules/productosProfesionales").then((m) => ({
    default: m.ProductosProfesionalesPage,
  }))
);
const ClientesPage = lazy(() =>
  import("./modules/clientes").then((m) => ({ default: m.ClientesPage }))
);
const ProfesionalesPage = lazy(() =>
  import("./modules/profesionales").then((m) => ({ default: m.ProfesionalesPage }))
);
const PedidosPage = lazy(() =>
  import("./modules/pedidos").then((m) => ({ default: m.PedidosPage }))
);
const EstadisticasPage = lazy(() =>
  import("./modules/estadisticas").then((m) => ({ default: m.EstadisticasPage }))
);
const ConfiguracionPage = lazy(() =>
  import("./modules/admin").then((m) => ({ default: m.ConfiguracionPage }))
);

const RouteFallback = ({ text }: { text: string }) => {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
        flexDirection: "column",
        gap: "16px",
      }}
    >
      <Loader size="lg" />
      <p style={{ color: "#6b7280", fontSize: "16px" }}>{text}</p>
    </div>
  );
};

function App() {
  return (
    <CustomProvider theme="light" locale={esES}>
      <div className="App">
        <Routes>
          <Route path="/" element={<Navigate to="/login" replace />} />
          <Route
            path="/login"
            element={
              <Suspense fallback={<RouteFallback text="Cargando..." />}>
                <Login />
              </Suspense>
            }
          />

          {/* Dashboard/Inicio */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Layout>
                  <Suspense fallback={<RouteFallback text="Cargando dashboard..." />}>
                    <Home />
                  </Suspense>
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* Recursos - Solo DEV y OWNER */}
          <Route
            path="/recursos"
            element={
              <ProtectedRoute requiredLevel={2}>
                <Layout>
                  <Suspense fallback={<RouteFallback text="Cargando..." />}>
                    <RecursosPage />
                  </Suspense>
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* Productos */}
          <Route
            path="/productos"
            element={
              <ProtectedRoute>
                <Layout>
                  <Suspense fallback={<RouteFallback text="Cargando..." />}>
                    <ProductosPage />
                  </Suspense>
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* Productos Profesionales - Solo DEV y OWNER */}
          <Route
            path="/productos-profesionales"
            element={
              <ProtectedRoute requiredLevel={2}>
                <Layout>
                  <Suspense fallback={<RouteFallback text="Cargando..." />}>
                    <ProductosProfesionalesPage />
                  </Suspense>
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* Clientes */}
          <Route
            path="/clientes"
            element={
              <ProtectedRoute>
                <Layout>
                  <Suspense fallback={<RouteFallback text="Cargando..." />}>
                    <ClientesPage />
                  </Suspense>
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* Profesionales */}
          <Route
            path="/profesionales"
            element={
              <ProtectedRoute>
                <Layout>
                  <Suspense fallback={<RouteFallback text="Cargando..." />}>
                    <ProfesionalesPage />
                  </Suspense>
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* Pedidos */}
          <Route
            path="/pedidos"
            element={
              <ProtectedRoute>
                <Layout>
                  <Suspense fallback={<RouteFallback text="Cargando..." />}>
                    <PedidosPage />
                  </Suspense>
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* Estadísticas - Acceso controlado por canAccess (permite USER/REGULAR) */}
          <Route
            path="/estadisticas"
            element={
              <ProtectedRoute requiredLevel={0}>
                <Layout>
                  <Suspense fallback={<RouteFallback text="Cargando..." />}>
                    <EstadisticasPage />
                  </Suspense>
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* Configuración - ADMIN, OWNER y DEV */}
          <Route
            path="/configuracion"
            element={
              <ProtectedRoute requiredLevel={1}>
                <Layout>
                  <Suspense fallback={<RouteFallback text="Cargando..." />}>
                    <ConfiguracionPage />
                  </Suspense>
                </Layout>
              </ProtectedRoute>
            }
          />
        </Routes>
      </div>
    </CustomProvider>
  );
}

export default App;
